import {
  Building2, Mail, Phone, MapPin, Award, Truck 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';

// Mock supplier profile data
const supplierProfile = {
  companyName: 'Sysco Miami',
  supplierId: 'sup-sysco',
  contactName: 'Robert Johnson',
  contactEmail: 'robert.johnson@sysco.com',
  contactPhone: '+1 (305) 555-0123',
  address: '1234 Distribution Way, Miami, FL 33166',
  categories: ['Produce', 'Seafood', 'Dairy', 'Dry Goods'],
  deliveryZones: ['Port of Miami', 'Port Everglades', 'Port Canaveral'],
  certifications: ['HACCP', 'FDA Registered', 'Organic Certified', 'SQF Level 3'],
  rating: 4.8,
  onTimeDeliveryRate: 96.2,
  qualityScore: 98.5,
  memberSince: '2019-03-15',
};

export function CompanyProfilePage() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Company Profile</h1>
          <p className="text-muted-foreground">Manage your company information and capabilities</p>
        </div>
        <Button>Edit Profile</Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Company Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Company Name</p>
                  <p className="font-medium">{supplierProfile.companyName}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Supplier ID</p>
                  <p className="font-medium">{supplierProfile.supplierId}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Contact Name</p>
                  <p className="font-medium">{supplierProfile.contactName}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Member Since</p>
                  <p className="font-medium">
                    {new Date(supplierProfile.memberSince).toLocaleDateString('en-US', {
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                </div>
              </div>

              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>{supplierProfile.contactEmail}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{supplierProfile.contactPhone}</span>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{supplierProfile.address}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Product Categories</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {supplierProfile.categories.map((category) => (
                  <span
                    key={category}
                    className="rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary"
                  >
                    {category}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5" />
                Delivery Zones
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {supplierProfile.deliveryZones.map((zone) => (
                  <div
                    key={zone}
                    className="flex items-center gap-2 rounded-lg border p-3"
                  >
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{zone}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5" />
                Certifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {supplierProfile.certifications.map((cert) => (
                  <div
                    key={cert}
                    className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 p-3"
                  >
                    <Award className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-800">{cert}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Performance Metrics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center p-4 rounded-lg bg-muted/50">
                <p className="text-3xl font-bold text-primary">{supplierProfile.rating}</p>
                <p className="text-sm text-muted-foreground">Overall Rating</p>
                <div className="flex justify-center mt-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className={`text-lg ${star <= Math.round(supplierProfile.rating) ? 'text-yellow-500' : 'text-gray-300'}`}
                    >
                      ★
                    </span>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">On-Time Delivery</span>
                    <span className="font-medium">{supplierProfile.onTimeDeliveryRate}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted">
                    <div
                      className="h-2 rounded-full bg-green-500"
                      style={{ width: `${supplierProfile.onTimeDeliveryRate}%` }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Quality Score</span>
                    <span className="font-medium">{supplierProfile.qualityScore}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted">
                    <div
                      className="h-2 rounded-full bg-blue-500"
                      style={{ width: `${supplierProfile.qualityScore}%` }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button variant="outline" className="w-full justify-start">
                <Award className="h-4 w-4 mr-2" />
                Add Certification
              </Button>
              <Button variant="outline" className="w-full justify-start">
                <Truck className="h-4 w-4 mr-2" />
                Add Delivery Zone
              </Button>
              <Button variant="outline" className="w-full justify-start">
                <Mail className="h-4 w-4 mr-2" />
                Update Contact Info
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
